import { Button } from '@/components/ui/button';
import {
  Filter,
  MapPin,
  X,
  Search,
  Calendar,
  Users,
  ChevronsUpDown,
  Check,
  Building2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import useSearch from '@/hooks/useSearch';
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { destinationsList as destinations } from '@/repositories/data/destination';
import { perks as PERKS } from '@/components/services/PropertyService';
import { areaList } from '@/repositories/data/area';
import { Input } from '@/components/ui/input';
import Price from '../Properties/Price';
import Perk from '../Properties/Perk';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { convertToEGP } from '@/lib/currency';

const getDestinationLabel = (name: string) => {
  return destinations.find((dest) => dest.name === name)?.label || name;
};

const getAreaLabel = (destination: string, area: string) => {
  return (
    areaList[destination as keyof typeof areaList]?.find((a) => a.name === area)?.label || area
  );
};

// Reusable Destination Sheet Component
const DestinationSheet = ({
  isOpen,
  onClose,
  selectedDestination,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedDestination: string | null;
  onSelect: (_destination: string | null) => void;
}) => (
  <Sheet open={isOpen} onOpenChange={onClose}>
    <SheetContent side="bottom" className="h-[70vh] max-h-[70vh] p-0 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-lg font-extrabold uppercase font-milligram">
            Choose Destination
          </SheetTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div
            className={`flex items-center justify-between p-4 rounded-lg border border-primary hover:bg-gray-50 cursor-pointer ${selectedDestination === null ? 'bg-primary text-secondary' : ''}`}
            onClick={() => {
              onSelect(null);
              onClose();
            }}
          >
            <span className="text-base font-medium">Anywhere</span>
            {!selectedDestination && <Check className="h-5 w-5 text-secondary" />}
          </div>
          {destinations.map((destination) => (
            <div
              key={destination.name}
              className={`flex items-center justify-between p-4 rounded-lg border border-primary hover:bg-gray-50 cursor-pointer ${selectedDestination === destination.name ? 'bg-primary text-secondary' : ''}`}
              onClick={() => {
                onSelect(destination.name);
                onClose();
              }}
            >
              <span className="text-base font-medium">{destination.label}</span>
              {selectedDestination === destination.name && (
                <Check className="h-5 w-5 text-secondary" />
              )}
            </div>
          ))}
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

// Reusable Calendar Sheet Component
const CalendarSheet = ({
  isOpen,
  onClose,
  dateRange,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange | undefined;
  onSelect: (_value: DateRange | undefined) => void;
}) => (
  <Sheet open={isOpen} onOpenChange={onClose}>
    <SheetContent side="bottom" className="h-[80vh] max-h-[80vh] p-0 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-lg font-milligram font-extrabold uppercase">
            Select Dates
          </SheetTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-0 flex items-center justify-center w-full">
        <CalendarComponent
          mode="range"
          min={1}
          required={false}
          defaultMonth={dateRange?.from ? new Date(dateRange.from) : new Date()}
          selected={dateRange}
          disabled={{
            from: new Date(new Date().setFullYear(new Date().getFullYear() - 10)),
            to: new Date(new Date().setDate(new Date().getDate() - 1)),
          }}
          onSelect={(value) => {
            onSelect(value as DateRange);
          }}
          onClose={onClose}
          numberOfMonths={1}
        />
      </div>
    </SheetContent>
  </Sheet>
);

// Reusable Area Sheet Component
const AreaSheet = ({
  isOpen,
  onClose,
  selectedArea,
  selectedDestination,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedArea: string | null;
  selectedDestination: string | null;
  onSelect: (_area: string | null) => void;
}) => (
  <Sheet open={isOpen} onOpenChange={onClose}>
    <SheetContent side="bottom" className="h-[80vh] max-h-[80vh] p-0 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-lg font-milligram font-extrabold uppercase">
            Choose Area
          </SheetTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <Command>
          <CommandInput placeholder="Search areas..." className="h-12" />
          <CommandList className="mt-4">
            <CommandEmpty>No areas found for this destination.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="any-area"
                value="any-area"
                onSelect={() => {
                  onSelect(null);
                  onClose();
                }}
                className="py-4"
              >
                {!selectedArea ? (
                  <Check className="mr-3 h-5 w-5" />
                ) : (
                  <div className="w-5 h-5 mr-3" />
                )}
                Any area
              </CommandItem>
              {areaList[selectedDestination as keyof typeof areaList]?.map((area) => (
                <CommandItem
                  key={area.name}
                  value={area.name}
                  onSelect={(currentValue) => {
                    onSelect(currentValue as string);
                    onClose();
                  }}
                  className="py-4"
                >
                  {selectedArea && selectedArea === area.name ? (
                    <Check className="mr-3 h-5 w-5" />
                  ) : (
                    <div className="w-5 h-5 mr-3" />
                  )}
                  {area.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </div>
    </SheetContent>
  </Sheet>
);

// Reusable Guests Sheet Component
const GuestsSheet = ({
  isOpen,
  onClose,
  guestCount,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  guestCount: number;
  onSelect: (_count: number) => void;
}) => (
  <Sheet open={isOpen} onOpenChange={onClose}>
    <SheetContent side="bottom" className="h-[50vh] max-h-[50vh] p-0 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-lg font-milligram font-extrabold uppercase">
            Number of Guests
          </SheetTitle>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center">
        <div className="space-y-6 w-full max-w-sm">
          <div className="flex items-center gap-12 justify-center">
            <Button
              variant="outline"
              size="lg"
              className="h-12 w-12 rounded-full"
              onClick={() => {
                if (guestCount > 1) {
                  onSelect(guestCount - 1);
                }
              }}
              disabled={guestCount <= 1}
            >
              -
            </Button>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">{guestCount}</div>
              <div className="text-lg text-gray-600">{guestCount === 1 ? 'Guest' : 'Guests'}</div>
            </div>
            <Button
              variant="outline"
              size="lg"
              className="h-12 w-12 rounded-full"
              onClick={() => {
                if (guestCount < 10) {
                  onSelect(guestCount + 1);
                }
              }}
              disabled={guestCount >= 10}
            >
              +
            </Button>
          </div>

          <Button className="w-full h-12" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

// Hero Mobile Search Bar Component
const HeroMobileSearchBar = () => {
  const { state, dispatch, goToExplore } = useSearch();
  const [isDestinationSheetOpen, setIsDestinationSheetOpen] = useState(false);
  const [isCalendarSheetOpen, setIsCalendarSheetOpen] = useState(false);
  const [isGuestsSheetOpen, setIsGuestsSheetOpen] = useState(false);

  return (
    <div className="space-y-2 text-primary">
      <div className="p-2 rounded-xl shadow-lg backdrop-blur-sm shadow-2xl backdrop-brightness-99 bg-gradient-to-b from-secondary/10 via-brand-blue/40 to-secondary/30 w-11/12 mx-auto flex flex-col">
        <div className="flex items-center gap-1 flex-col">
          {/* Destination */}
          <Sheet open={isDestinationSheetOpen} onOpenChange={setIsDestinationSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="Where to?"
                variant="ghost"
                className={cn('flex-1 justify-start text-left font-normal h-auto p-2 w-full gap-2')}
              >
                <MapPin className={cn('w-8 h-8 mr-1 text-primary')} strokeWidth={3} />
                <div className="truncate text-bold text-primary text-base flex flex-col">
                  <div className="text-primary font-extrabold">Where to?</div>
                  <div className="normal-case text-primary">
                    {state.destination ? getDestinationLabel(state.destination) : 'Anywhere'}
                  </div>
                </div>
              </Button>
            </SheetTrigger>
            <DestinationSheet
              isOpen={isDestinationSheetOpen}
              onClose={() => setIsDestinationSheetOpen(false)}
              selectedDestination={state.destination}
              onSelect={(destination) =>
                dispatch({ type: 'SET_DESTINATION', payload: destination })
              }
            />
          </Sheet>

          <Separator orientation="horizontal" className="h-px bg-primary/60 w-full mx-auto" />

          {/* Calendar */}
          <Sheet open={isCalendarSheetOpen} onOpenChange={setIsCalendarSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="When?"
                variant="ghost"
                className={cn('flex-1 justify-start text-left font-normal h-auto p-2 w-full gap-2')}
              >
                <Calendar className="w-8 h-8 mr-1 text-primary" strokeWidth={3} />
                <div className="truncate text-primary text-base flex flex-col">
                  <span className="text-primary font-extrabold">When?</span>
                  <span className="normal-case text-primary">
                    {state.dateRange?.from ? format(state.dateRange.from, 'MMM dd, y') : 'Anytime'}
                    {state.dateRange?.to ? ` - ${format(state.dateRange.to, 'MMM dd, y')}` : ''}
                  </span>
                </div>
              </Button>
            </SheetTrigger>
            <CalendarSheet
              isOpen={isCalendarSheetOpen}
              onClose={() => setIsCalendarSheetOpen(false)}
              dateRange={state.dateRange}
              onSelect={(value) =>
                dispatch({ type: 'SET_DATE_RANGE', payload: value as DateRange })
              }
            />
          </Sheet>

          <Separator orientation="horizontal" className="h-px bg-primary/60 w-full mx-auto" />

          {/* Guests */}
          <Sheet open={isGuestsSheetOpen} onOpenChange={setIsGuestsSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="Who's Coming?"
                variant="ghost"
                className="flex-1 justify-start text-left font-normal h-auto p-2 w-full gap-2"
              >
                <Users className="w-8 h-8 mr-1 text-primary" strokeWidth={3} />
                <span className="text-base text-primary">
                  <div className="text-primary font-extrabold">Who's Coming?</div>
                  <div className="normal-case text-primary">
                    {state.guests} {state.guests === 1 ? 'Guest' : 'Guests'}
                  </div>
                </span>
              </Button>
            </SheetTrigger>
            <GuestsSheet
              isOpen={isGuestsSheetOpen}
              onClose={() => setIsGuestsSheetOpen(false)}
              guestCount={state.guests}
              onSelect={(count) => dispatch({ type: 'SET_GUESTS', payload: count })}
            />
          </Sheet>
        </div>

        {/* Yalla Button */}
        <div className="flex items-center justify-center mt-4">
          <Button
            variant="default"
            size="lg"
            aria-label="Yalla"
            className="px-8 text-xl"
            onClick={() =>
              goToExplore({
                destination: state.destination,
                area: state.area,
                dateRange: state.dateRange,
                guests: state.guests,
              })
            }
          >
            Yalla
          </Button>
        </div>
      </div>
    </div>
  );
};

// Explore Mobile Search Bar Component
const ExploreMobileSearchBar = () => {
  const { state, dispatch, activeFilterCount, goToExplore } = useSearch();
  const [isDestinationSheetOpen, setIsDestinationSheetOpen] = useState(false);
  const [isAreaSheetOpen, setIsAreaSheetOpen] = useState(false);
  const [isCalendarSheetOpen, setIsCalendarSheetOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isGuestsSheetOpen, setIsGuestsSheetOpen] = useState(false);

  const clearFilters = () => {
    dispatch({ type: 'CLEAR_ALL' });
  };

  return (
    <div className="space-y-4 text-primary">
      {/* Main Search Bar - Enhanced Design */}
      <div className="p-4 rounded-2xl shadow-xl backdrop-blur-md bg-white/95 border border-gray-100 md:w-11/12 mx-auto">
        {/* Search Fields Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Destination */}
          <Sheet open={isDestinationSheetOpen} onOpenChange={setIsDestinationSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="Where?"
                variant="ghost"
                className="h-16 flex flex-col items-start justify-center p-3 rounded-xl border border-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200"
              >
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-primary" strokeWidth={2} />
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Where
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 truncate w-full text-left">
                  {state.destination ? getDestinationLabel(state.destination) : 'Anywhere'}
                </span>
              </Button>
            </SheetTrigger>
            <DestinationSheet
              isOpen={isDestinationSheetOpen}
              onClose={() => setIsDestinationSheetOpen(false)}
              selectedDestination={state.destination}
              onSelect={(destination) =>
                dispatch({ type: 'SET_DESTINATION', payload: destination })
              }
            />
          </Sheet>

          {/* Area */}
          <Sheet open={isAreaSheetOpen} onOpenChange={setIsAreaSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="Area"
                variant="ghost"
                className={cn(
                  'h-16 flex flex-col items-start justify-center p-3 rounded-xl border border-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200',
                  !state.destination && 'opacity-50 cursor-not-allowed bg-gray-50'
                )}
                disabled={!state.destination}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Area
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 truncate w-full text-left">
                  {state.destination && state.area
                    ? getAreaLabel(state.destination, state.area)
                    : 'Any area'}
                </span>
              </Button>
            </SheetTrigger>
            <AreaSheet
              isOpen={isAreaSheetOpen}
              onClose={() => setIsAreaSheetOpen(false)}
              selectedArea={state.area}
              selectedDestination={state.destination}
              onSelect={(area) => dispatch({ type: 'SET_AREA', payload: area })}
            />
          </Sheet>

          {/* Calendar */}
          <Sheet open={isCalendarSheetOpen} onOpenChange={setIsCalendarSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="When?"
                variant="ghost"
                className="h-16 flex flex-col items-start justify-center p-3 rounded-xl border border-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    When
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 truncate w-full text-left">
                  {state.dateRange?.from ? format(state.dateRange.from, 'MMM dd') : 'Anytime'}
                  {state.dateRange?.to ? ` - ${format(state.dateRange.to, 'MMM dd')}` : ''}
                </span>
              </Button>
            </SheetTrigger>
            <CalendarSheet
              isOpen={isCalendarSheetOpen}
              onClose={() => setIsCalendarSheetOpen(false)}
              dateRange={state.dateRange}
              onSelect={(value) =>
                dispatch({ type: 'SET_DATE_RANGE', payload: value as DateRange })
              }
            />
          </Sheet>

          {/* Guests */}
          <Sheet open={isGuestsSheetOpen} onOpenChange={setIsGuestsSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label="Guests"
                variant="ghost"
                className="h-16 flex flex-col items-start justify-center p-3 rounded-xl border border-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Guests
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 truncate w-full text-left">
                  {state.guests} {state.guests === 1 ? 'Guest' : 'Guests'}
                </span>
              </Button>
            </SheetTrigger>
            <GuestsSheet
              isOpen={isGuestsSheetOpen}
              onClose={() => setIsGuestsSheetOpen(false)}
              guestCount={state.guests}
              onSelect={(count) => dispatch({ type: 'SET_GUESTS', payload: count })}
            />
          </Sheet>
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center gap-3">
          {/* Search Button */}
          <Button
            variant="default"
            size="lg"
            className="flex-1 "
            onClick={() =>
              goToExplore({
                destination: state.destination,
                area: state.area,
                dateRange: state.dateRange,
                guests: state.guests,
              })
            }
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>

          {/* Filters Button */}
          <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="lg"
                className="px-4 border-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200 relative"
              >
                <Filter className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <div className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {activeFilterCount}
                  </div>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[90vh] max-h-[90vh] p-0 flex flex-col">
              {/* Sticky Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-primary px-6 py-4">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg font-semibold">Filters</SheetTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsFiltersOpen(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {activeFilterCount > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-600">
                      {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-6 px-2 text-xs text-primary hover:text-primary/80"
                    >
                      Clear all
                    </Button>
                  </div>
                )}
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Sort */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-900">Sort by</Label>
                  <Select
                    value={state.sort || 'trending'}
                    onValueChange={(value) => dispatch({ type: 'SET_SORT', payload: value })}
                  >
                    <SelectTrigger className="h-10 rounded-full">
                      <ChevronsUpDown className="w-4 h-4 mr-2" />
                      <SelectValue>
                        {state.sort === 'trending' && 'Trending'}
                        {state.sort === 'newest' && 'Newest'}
                        {state.sort === 'updated' && 'Updated'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trending">Trending</SelectItem>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="updated">Updated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Price Range */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-900">Price Range</Label>
                  <div className="flex gap-3">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-gray-600">Minimum</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        min="0"
                        className="h-10"
                        value={state.minPrice || ''}
                        onChange={(e) => {
                          const minValue = parseInt(e.target.value) || 0;
                          dispatch({ type: 'SET_MIN_PRICE', payload: minValue });
                          if (state.maxPrice && state.maxPrice <= minValue) {
                            dispatch({ type: 'SET_MAX_PRICE', payload: 0 });
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-gray-600">Maximum</Label>
                      <Input
                        type="number"
                        placeholder="No limit"
                        min={state.minPrice ? state.minPrice + 1 : 1}
                        className="h-10"
                        value={state.maxPrice || ''}
                        onChange={(e) => {
                          const maxValue = parseInt(e.target.value) || 0;
                          dispatch({ type: 'SET_MAX_PRICE', payload: maxValue });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Bedrooms & Bathrooms */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-900">Property Details</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">Bedrooms</Label>
                      <Select
                        value={state.minBeds.toString()}
                        onValueChange={(value) =>
                          dispatch({ type: 'SET_MIN_BEDS', payload: parseInt(value) })
                        }
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Any</SelectItem>
                          <SelectItem value="1">1+</SelectItem>
                          <SelectItem value="2">2+</SelectItem>
                          <SelectItem value="3">3+</SelectItem>
                          <SelectItem value="4">4+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600">Bathrooms</Label>
                      <Select
                        value={state.minBaths.toString()}
                        onValueChange={(value) =>
                          dispatch({ type: 'SET_MIN_BATHS', payload: parseInt(value) })
                        }
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Any</SelectItem>
                          <SelectItem value="1">1+</SelectItem>
                          <SelectItem value="2">2+</SelectItem>
                          <SelectItem value="3">3+</SelectItem>
                          <SelectItem value="4">4+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Perks */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-900">Amenities</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {PERKS.map((perk) => (
                      <Perk
                        key={perk}
                        perk={perk}
                        onClick={() => dispatch({ type: 'TOGGLE_PERK', payload: perk })}
                        type={state.perks.includes(perk) ? 'default' : 'outline'}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="sticky bottom-0 z-10 bg-white border-t border-primary px-6 py-4 space-y-3">
                {activeFilterCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="w-full h-11 text-gray-700 border-gray-300 hover:bg-gray-50"
                  >
                    Clear all filters
                  </Button>
                )}
                <Button
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-medium"
                  onClick={() => setIsFiltersOpen(false)}
                >
                  Apply Filters ({activeFilterCount} applied)
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active Filters - Compact Design */}
      {activeFilterCount > 0 && (
        <div className="w-11/12 mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-gray-700">Active Filters:</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear all
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {state.destination && (
              <Badge variant="secondary" className="text-xs">
                {getDestinationLabel(state.destination)}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => dispatch({ type: 'SET_DESTINATION', payload: null })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            {state.area && (
              <Badge variant="secondary" className="text-xs">
                {getAreaLabel(state.destination || '', state.area)}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => dispatch({ type: 'SET_AREA', payload: null })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            {state.dateRange?.from && (
              <Badge variant="secondary" className="text-xs">
                {format(state.dateRange.from, 'MMM dd')}
                {state.dateRange.to && ` - ${format(state.dateRange.to, 'MMM dd')}`}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() =>
                    dispatch({
                      type: 'SET_DATE_RANGE',
                      payload: { from: undefined, to: undefined },
                    })
                  }
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            {state.minPrice > 0 && state.maxPrice && state.maxPrice > 0 ? (
              <Badge variant="secondary" className="text-xs flex items-center">
                <span className="mr-1">Between</span>
                <Price
                  price={convertToEGP(state.minPrice, state.currency)}
                  currency={state.currency}
                />
                -
                <Price
                  price={convertToEGP(state.maxPrice, state.currency)}
                  currency={state.currency}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => {
                    dispatch({ type: 'SET_MIN_PRICE', payload: 0 });
                    dispatch({ type: 'SET_MAX_PRICE', payload: 0 });
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ) : null}
            {state.minPrice > 0 &&
            (state.maxPrice == null || state.maxPrice <= 0 || isNaN(state.maxPrice)) ? (
              <Badge variant="secondary" className="text-xs flex items-center">
                <span className="mr-1">Min</span>
                <Price
                  price={convertToEGP(state.minPrice, state.currency)}
                  currency={state.currency}
                />
                <Button
                  variant="ghost"
                  aria-label="Clear minimum price"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => dispatch({ type: 'SET_MIN_PRICE', payload: 0 })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ) : null}

            {state.maxPrice &&
            state.maxPrice > 0 &&
            (state.minPrice == null || state.minPrice == 0 || isNaN(state.minPrice)) ? (
              <Badge variant="secondary" className="text-xs flex items-center">
                <span className="mr-1">Max</span>
                <Price
                  price={convertToEGP(state.maxPrice, state.currency)}
                  currency={state.currency}
                />
                <Button
                  variant="ghost"
                  aria-label="Clear maximum price"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => dispatch({ type: 'SET_MAX_PRICE', payload: 0 })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ) : null}
            {state.perks.slice(0, 3).map((perk) => (
              <Badge key={perk} variant="secondary" className="text-xs">
                {perk}
                <Button
                  variant="ghost"
                  aria-label="Clear perk"
                  size="sm"
                  className="h-auto p-0 ml-1 hover:bg-transparent"
                  onClick={() => dispatch({ type: 'TOGGLE_PERK', payload: perk })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
            {state.perks.length > 3 && (
              <Badge
                variant="secondary"
                className="text-xs px-2 py-1 bg-primary/10 text-primary border-primary/20"
              >
                +{state.perks.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Mobile Search Bar Component
export const MobileSearchBar = ({ isHero }: { isHero?: boolean }) => {
  if (isHero) {
    return <HeroMobileSearchBar />;
  }

  return <ExploreMobileSearchBar />;
};
